import { fbm2 } from '@vm/shared';
import { family, M } from '../palette';
import { blocks, MapBuilder, planks, smoothField, solid, symPoint, symYaw, yawToward } from './builder';
import { emptyMeta, type MapDef, type MapMeta } from './types';

const S = 640;
const G = 32;
const SYM = 'mirrorX' as const;
const LAKE_R = 96;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** 1 inside the rectangle, fading to 0 over `pad` voxels outside it. */
const rectW = (x: number, z: number, x0: number, z0: number, x1: number, z1: number, pad: number): number =>
  clamp01(1 - Math.max(x0 - x, x - x1, z0 - z, z - z1) / pad);

/** Snow drifts: flat in the play spaces, rolling banks toward the map edges. */
function terrain(b: MapBuilder): void {
  const h = new Int16Array(S * S);
  const surface = new Uint8Array(S * S);
  const snow1 = M('snow_1');
  const snow2 = M('snow_2');
  const drifts = smoothField(S, S, 4, (x, z) => fbm2(x * 0.018, z * 0.018, 91, 3));
  const patchesN = smoothField(S, S, 4, (x, z) => fbm2(x * 0.05, z * 0.05, 7, 2));
  for (let z = 0; z < S; z++)
    for (let x = 0; x < S; x++) {
      const mx = x < S / 2 ? x : S - 1 - x;
      const edge = Math.min(mx, z, S - 1 - z);
      const n = drifts(mx, z);
      const edgeW = clamp01((96 - edge) / 70);
      const dl = Math.hypot(mx - 319.5, z - 319.5);
      const dh = Math.hypot(mx - 319.5, z - 529.5);
      const flat = Math.max(
        rectW(mx, z, 12, 178, 206, 462, 18),
        rectW(mx, z, 246, 40, 330, 160, 18),
        rectW(mx, z, 26, 458, 136, 524, 12),
        clamp01((96 - dh) / 18),
        clamp01((126 - dl) / 18),
      );
      const drift = edgeW * (4 + n * 22) * (1 - flat);
      h[x + z * S] = G + Math.floor(drift / 4) * 4;
      surface[x + z * S] = patchesN(mx, z) > 0.62 ? snow2 : snow1;
    }
  b.heightfield({ height: h, surface, sub: [snow2, snow1], subDepth: 3, deep: M('rock_1') });
}

/** Palisade of upright logs along x or z with sharpened tops. */
function palisade(b: MapBuilder, x0: number, z0: number, x1: number, z1: number): void {
  const alongX = x1 - x0 > z1 - z0;
  const n = alongX ? x1 - x0 : z1 - z0;
  for (let i = 0; i < n; i += 3) {
    const tall = 22 + ((i * 7) % 5);
    const log = (i / 3) % 2 === 0 ? M('log_1') : M('log_2');
    if (alongX) {
      const x = x0 + i;
      b.box(x, G, z0, Math.min(x1, x + 3), G + tall, z0 + 3, log);
      b.box(x + 1, G + tall, z0 + 1, Math.min(x1, x + 2), G + tall + 2, z0 + 2, log);
    } else {
      const z = z0 + i;
      b.box(x0, G, z, x0 + 3, G + tall, Math.min(z1, z + 3), log);
      b.box(x0 + 1, G + tall, z + 1, x0 + 2, G + tall + 2, Math.min(z1, z + 2), log);
    }
  }
}

function snowman(b: MapBuilder, x: number, z: number): void {
  const snow = M('snow_1');
  b.sphere(x, G + 5, z, 6.2, snow);
  b.sphere(x, G + 14, z, 4.6, snow);
  b.sphere(x, G + 21, z, 3.5, snow);
  b.box(x - 2, G + 22, z + 3, x - 1, G + 23, z + 4, M('paint_black'));
  b.box(x + 1, G + 22, z + 3, x + 2, G + 23, z + 4, M('paint_black'));
  b.box(x, G + 20, z + 3, x + 1, G + 21, z + 7, M('paint_orange'));
  for (const y of [G + 12, G + 15]) b.box(x, y, z + 4, x + 1, y + 1, z + 5, M('paint_black'));
  b.cylinder(x, z, G + 24, G + 25, 4, M('paint_black'));
  b.cylinder(x, z, G + 25, G + 30, 2.6, M('paint_black'));
  b.box(x - 2, G + 25, z - 3, x + 3, G + 26, z + 3, M('paint_red'));
  b.line(x - 4, G + 15, z, x - 10, G + 20, z, M('log_2'));
  b.line(x + 4, G + 15, z, x + 10, G + 20, z, M('log_2'));
}

function snowmobile(b: MapBuilder, x: number, z: number, color: number): void {
  b.box(x, G, z, x + 2, G + 1, z + 30, M('steel_dark_1'));
  b.box(x + 12, G, z, x + 14, G + 1, z + 30, M('steel_dark_1'));
  b.box(x + 1, G + 2, z + 4, x + 13, G + 9, z + 26, color);
  b.box(x + 3, G + 9, z + 12, x + 11, G + 11, z + 22, M('paint_black'));
  b.box(x + 2, G + 9, z + 7, x + 12, G + 14, z + 8, M('glass_tint'));
  b.box(x + 4, G + 1, z + 18, x + 10, G + 3, z + 30, M('rubber'));
}

/** Team 0 half (west); the east half is its mirror. */
function westHalf(b: MapBuilder): void {
  const packed = solid([M('snow_2')]);
  // Compound yard: packed snow inside the palisade.
  b.box(22, G - 1, 190, 196, G, 452, packed);
  palisade(b, 20, 186, 196, 189);
  palisade(b, 20, 452, 196, 455);
  palisade(b, 20, 189, 23, 452);
  palisade(b, 193, 189, 196, 290);
  palisade(b, 193, 350, 196, 452);
  // Gate posts and a lintel beam over the east gate.
  for (const z of [286, 350]) b.box(191, G, z, 198, G + 34, z + 4, M('log_2'));
  b.box(191, G + 30, 286, 198, G + 34, 354, M('log_2'));
  b.box(193, G + 34, 300, 196, G + 44, 340, M('team_red'));

  // Main cabin: two floors of logs under a snowy roof.
  b.building({
    x0: 44, x1: 128, z0: 212, z1: 280, y0: G - 1, floors: 2, wall: M('log_1'), roof: M('snow_1'), trim: M('stone_4'),
    floor: planks([M('planks_2')], 0), windows: { w: 8, h: 9, sill: 9, spacing: 18, glass: M('glass_tint') },
    doors: [{ side: 's', w: 12 }, { side: 'e', w: 12 }],
  });
  // Porch roof over the south door and a chimney.
  b.box(70, G + 24, 280, 104, G + 26, 292, M('snow_1'));
  for (const x of [70, 101]) b.box(x, G, 289, x + 3, G + 24, 292, M('log_2'));
  // Stone chimney against the west wall with a glowing fireplace on the ground floor.
  b.box(46, G, 234, 56, G + 92, 246, blocks([M('stone_1')], 3, 0));
  b.clear(50, G, 237, 56, G + 9, 243);
  b.box(50, G, 238, 53, G + 2, 242, M('neon_orange'));
  // Icicles hanging from the north eave.
  for (let x = 46; x < 126; x += 5) b.box(x, G + 55 - (x % 3), 211, x + 1, G + 60, 212, M('ice'));

  // Garage shed with a snowmobile, big door facing the yard.
  b.building({
    x0: 52, x1: 128, z0: 368, z1: 432, y0: G - 1, floors: 1, wall: planks([M('planks_dark_1')], 0), roof: M('snow_1'), roofStyle: 'gableX',
    trim: M('stone_4'), windows: { w: 8, h: 7, sill: 11, spacing: 20, glass: null }, doors: [{ side: 'n', w: 32, h: 24 }],
  });
  snowmobile(b, 70, 380, M('team_red'));
  b.crate(104, G, 404);
  b.crate(104, G + 10, 406, 8);

  // Watchtower in the yard's north-east corner, stairs rising from the yard.
  const tx = 150;
  const tz = 196;
  for (const [dx, dz] of [[0, 0], [27, 0], [0, 27], [27, 27]]) b.box(tx + dx, G, tz + dz, tx + dx + 3, G + 62, tz + dz + 3, M('log_2'));
  b.line(tx + 1, G + 4, tz + 1, tx + 28, G + 56, tz + 1, M('log_1'), 2);
  b.line(tx + 1, G + 4, tz + 28, tx + 28, G + 56, tz + 28, M('log_1'), 2);
  b.box(tx - 3, G + 60, tz - 3, tx + 33, G + 62, tz + 33, planks([M('planks_1')], 0));
  b.walls(tx - 3, G + 62, tz - 3, tx + 33, G + 71, tz + 33, 1, planks([M('planks_dark_2')], 0));
  b.clear(tx + 18, G + 62, tz + 32, tx + 32, G + 71, tz + 33);
  for (const [dx, dz] of [[-3, -3], [32, -3], [-3, 32], [32, 32]]) b.box(tx + dx, G + 71, tz + dz, tx + dx + 1, G + 84, tz + dz + 1, M('log_2'));
  for (let k = 0; k < 7; k++) b.box(tx - 5 + k * 2, G + 84 + k, tz - 5 + k * 2, tx + 35 - k * 2, G + 85 + k, tz + 35 - k * 2, M('snow_1'));
  // A long straight flight from the yard up to a gap in the parapet (kept clear of props).
  b.stairs(tx + 19, G, tz + 94, 'n', 12, 62, planks([M('planks_2')], 0), 3);
  b.box(tx + 19, G + 58, tz + 30, tx + 31, G + 62, tz + 34, planks([M('planks_2')], 0));

  // Palisade baffle inside the gate: the spawn yard is never in a straight line of fire.
  palisade(b, 160, 266, 163, 374);

  // Sandbag nests facing the lake, crates and a fuel depot with explosive drums outside the wall.
  b.sandbags(206, 262, 214, 300, G, 9);
  b.sandbags(206, 340, 214, 378, G, 9);
  b.crate(160, G, 320);
  b.crate(170, G, 334, 8);
  for (const zz of [470, 500]) {
    b.cylinder(70, zz + 10, G + 6, G + 7, 2, M('steel_dark_1'));
    for (let x = 40; x < 110; x++)
      for (let dy = -10; dy <= 10; dy++)
        for (let dz = -10; dz <= 10; dz++) if (dy * dy + dz * dz <= 100) b.set(x, G + 12 + dy, zz + 10 + dz, (x - 40) % 24 < 3 ? M('paint_red') : M('paint_white'));
    for (const x of [48, 96]) b.box(x, G, zz + 4, x + 4, G + 4, zz + 16, M('steel_dark_1'));
  }
  b.tntBarrel(124, G, 488);
  b.tntBarrel(124, G, 500);

  // Ice blocks on the lake for cover, and the snow bank around the shore.
  b.box(262, G, 266, 276, G + 16, 282, M('ice'));
  b.box(250, G, 352, 260, G + 12, 368, M('ice'));
  b.box(292, G, 400, 304, G + 20, 408, M('ice'));

  // Forest and boulders around the edges.
  const pines: [number, number, number][] = [
    [40, 40, 64], [90, 60, 56], [150, 34, 70], [36, 120, 58], [224, 60, 60], [44, 560, 66], [100, 596, 58], [160, 560, 62], [40, 486, 56],
    [236, 580, 58], [214, 170, 50], [220, 470, 54],
  ];
  for (const [x, z, hgt] of pines) b.pineTree(x, b.surfaceY(x, z), z, hgt, true);
  for (const [x, z, r] of [[120, 120, 9], [186, 600, 11], [16, 330, 8], [240, 130, 7]]) b.rock(x, b.surfaceY(x, z) - 2, z, r, M('stone_3'));
  snowman(b, 112, 300);
  snowman(b, 250, 470);
}

/** Things on the center line (built in the west half so the mirror makes them symmetric). */
function center(b: MapBuilder): void {
  // Frozen lake: ice over water over a stony bed.
  const cx = 320;
  const cz = 320;
  for (let z = cz - LAKE_R - 12; z <= cz + LAKE_R + 12; z++)
    for (let x = cx - LAKE_R - 12; x < cx; x++) {
      const d = Math.sqrt((x - cx + 0.5) * (x - cx + 0.5) + (z - cz + 0.5) * (z - cz + 0.5));
      if (d <= LAKE_R) {
        const bed = G - 10 - Math.floor((LAKE_R - d) / 30);
        b.clear(x, bed, z, x + 1, G + 8, z + 1);
        b.box(x, bed - 1, z, x + 1, bed, z + 1, M('stone_4'));
        b.box(x, bed, z, x + 1, G - 2, z + 1, M('water'));
        b.box(x, G - 2, z, x + 1, G, z + 1, M('ice'));
      } else if (d <= LAKE_R + 12) {
        const bank = Math.floor((LAKE_R + 12 - d) / 4);
        if (bank > 0) b.box(x, G, z, x + 1, G + bank, z + 1, M('snow_1'));
      }
    }

  // Radar station: concrete block with stairs to the roof and a lattice mast carrying the dish.
  const conc = blocks(family('concrete'), 5, 2);
  b.building({
    x0: 272, x1: 368, z0: 56, z1: 136, y0: G - 1, floors: 1, wall: conc, trim: M('concrete_4'), roofStyle: 'flat', parapet: 4,
    windows: { w: 10, h: 6, sill: 12, spacing: 22, glass: M('glass_tint') }, doors: [{ side: 's', w: 14 }],
  });
  b.stairs(258, G, 150, 'n', 12, 31, M('concrete_2'));
  b.box(258, G + 30, 112, 272, G + 31, 120, M('concrete_2'));
  b.clear(272, G + 31, 108, 273, G + 36, 124);
  b.box(270, G + 31, 120, 272, G + 40, 152, M('paint_yellow'));
  const roof = G + 31;
  const top = roof + 104;
  b.lattice(310, roof, 86, 330, top, 106, 'y', M('metal_1'), 2, 16);
  b.box(306, top, 82, 334, top + 4, 110, M('steel_dark_1'));
  // Dish facing south over the lake.
  const dy0 = top + 22;
  for (let dy = -24; dy <= 24; dy++)
    for (let dx = -24; dx < 0; dx++) {
      const r2 = (dx + 0.5) * (dx + 0.5) + dy * dy;
      if (r2 > 576) continue;
      const back = Math.floor(r2 / 70);
      b.box(320 + dx, dy0 + dy, 96 - back, 321 + dx, dy0 + dy + 1, 98 - back, r2 > 500 ? M('metal_3') : M('hull_white'));
    }
  b.box(318, top + 4, 94, 320, dy0, 98, M('steel_dark_1'));
  b.line(318, dy0 - 18, 98, 319, dy0, 116, M('steel_dark_1'));
  b.box(316, dy0 - 2, 114, 320, dy0 + 2, 118, M('neon_red'));

  // Helipad in the south: concrete disc, landing ring and the H.
  for (let z = 460; z < 600; z++)
    for (let x = 250; x < 320; x++) {
      const d = Math.sqrt((x - 319.5) * (x - 319.5) + (z - 529.5) * (z - 529.5));
      if (d > 66) continue;
      b.set(x, G - 1, z, d > 62 ? M('paint_yellow') : M('concrete_2'));
      b.clear(x, G, z, x + 1, G + 8, z + 1);
    }
  b.box(292, G - 1, 500, 300, G, 560, M('paint_white'));
  b.box(300, G - 1, 526, 320, G, 534, M('paint_white'));
  for (const [x, z] of [[258, 529], [272, 484], [272, 575]]) b.box(x, G, z, x + 3, G + 3, z + 3, M('neon_red'));
  b.crate(232, G, 480);
  b.crate(232, G, 570, 8);
  b.sandbags(226, 500, 234, 560, G, 9);
}

function build(b: MapBuilder): MapMeta {
  terrain(b);
  westHalf(b);
  center(b);
  b.symmetrize(SYM);

  // Metadata ------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, 32, 32);
  const team0: [number, number][] = [
    [3, 30], [3, 32.4], [3, 34.8], [3, 37.2], [5.2, 31.2], [5.2, 33.6], [5.2, 36], [4.1, 28.4],
  ];
  for (const [x, z] of team0) {
    const yaw = -Math.PI / 2;
    meta.spawns.push({ team: 0, x, y: gy, z, yaw });
    const [mx, mz] = symPoint(SYM, S / 10, S / 10, x, z);
    meta.spawns.push({ team: 1, x: mx, y: gy, z: mz, yaw: symYaw(SYM, yaw) });
  }
  const ffa: [number, number][] = [
    [6, 16.5], [58, 16.5], [6, 48], [58, 48], [16.5, 36.5], [47.5, 36.5], [22, 9], [42, 9], [21, 55], [43, 55],
    [26, 22], [38, 22], [26, 42], [38, 42], [12, 58.5], [52, 58.5], [29, 4.5], [35, 4.5],
  ];
  for (const [x, z] of ffa) meta.spawns.push({ team: -1, x, y: b.standAt(x, z, 8), z, yaw: yawToward(x, z, 32, 32) });
  meta.points.push({ id: 'A', name: 'Radar', x: 32, y: gy, z: 15, r: 3.5 });
  meta.points.push({ id: 'B', name: 'Frozen Lake', x: 32, y: gy, z: 32, r: 4 });
  meta.points.push({ id: 'C', name: 'Helipad', x: 32, y: gy, z: 53, r: 4 });
  meta.flags.push({ team: 0, x: 13.5, y: gy, z: 32.5 }, { team: 1, x: 50.5, y: gy, z: 32.5 });
  for (const [x, z, kind] of [
    [14, 21.5, 'health'], [50, 21.5, 'health'], [14, 43.5, 'armor'], [50, 43.5, 'armor'], [32, 27.5, 'health'], [32, 45, 'health'],
  ] as const)
    meta.pickups.push({ kind, x, y: b.groundAt(x, z, 8), z });
  return meta;
}

export const frostbite: MapDef = {
  id: 'frostbite',
  name: 'Frostbite Outpost',
  description: 'Two log forts face off across a frozen lake under the radar mast. The ice will not hold forever.',
  size: [S, 192, S],
  modes: ['tdm', 'ffa', 'ctf', 'dom', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 4401,
  version: 2,
  theme: {
    skyTop: 0x86a9d2,
    skyHorizon: 0xe4ecf4,
    fog: 0xdce6ef,
    fogNear: 35,
    fogFar: 125,
    sun: [0.35, 0.5, -0.55],
    sunColor: 0xfff0e0,
    sunIntensity: 0.72,
    ambient: 0xbfd0ea,
    ambientIntensity: 0.52,
    outside: 0xe8eef4,
    music: 'snow',
  },
  build,
};

